/**
 * Plan de fusion : à partir de l'analyse et des choix de l'utilisateur sur les
 * conflits, produit la liste ORDONNÉE des opérations à appliquer au DWG cible.
 *
 * Règles d'identité (oplog) :
 *  - une entité appariée par handle garde l'uid de la cible ; si la branche
 *    fusionnée lui en avait donné un autre, un alias `fusionnée → cible` est posé
 *    (le fil réécrit les events de la branche absorbée) ;
 *  - une entité sans uid dans la cible reprend celui de la branche fusionnée ;
 *  - une copie ne peut jamais dupliquer un uid déjà présent dans le résultat
 *    (copie « garder les deux », héritage du bug de copie d'entités) : uid neuf.
 *
 * Correction automatique sur le résultat : rattachement à une observation
 * supprimée → détaché. Les n° de marqueurs photo ne sont jamais changés.
 *
 * Photos d'un marqueur : fusion 3 voies de la liste, sans conflit (un ajout d'un
 * côté est gardé, un retrait d'un côté est appliqué).
 */

import type {
  ConflictChoice,
  EntityDecision,
  EntityRef,
  EntitySnapshot,
  GeometryOutcome,
  MergeAnalysis,
  MergeConflict,
  MergeOp,
  MergePlan,
  NamedRecordKind,
  UidAlias,
} from "./types";

export interface MergePlanOptions {
  /**
   * `localId` des observations existantes. Fourni, tout rattachement à une autre
   * observation (supprimée entre-temps) est retiré du résultat.
   */
  validObservationIds?: ReadonlySet<string>;
}

export type MergeResolutions = Readonly<Record<string, ConflictChoice>>;

/** Conflits sans choix (le plan ne peut pas être construit tant qu'il en reste). */
export function unresolvedConflicts(
  analysis: MergeAnalysis,
  resolutions: MergeResolutions,
): MergeConflict[] {
  return analysis.conflicts.filter((c) => !resolutions[c.id]);
}

const GEOMETRY_BY_CHOICE: Record<
  "modify-modify" | "modify-delete" | "delete-modify",
  Record<ConflictChoice, GeometryOutcome>
> = {
  "modify-modify": { target: "target", merged: "merged", both: "both" },
  // « both » n'a pas de sens face à une suppression : on garde l'entité modifiée.
  "modify-delete": { target: "target", merged: "delete", both: "target" },
  "delete-modify": { target: "absent", merged: "restore", both: "restore" },
};

/**
 * Ordre d'application des éléments nommés : une sorte n'en référence que de
 * précédentes (un style de cote pointe un style de texte, un bloc des calques…).
 */
const RECORD_ORDER: NamedRecordKind[] = [
  "linetype",
  "textStyle",
  "layer",
  "dimStyle",
  "mleaderStyle",
  "block",
  "layout",
];

/** Entité du résultat, avec son état « tel que copié/présent » et l'état voulu. */
interface ResultEntity {
  ref: EntityRef;
  currentObservation: string | null;
  desiredObservation: string | null;
  currentPhotos: number[];
  desiredPhotos: number[];
}

interface PendingCopy {
  merged: EntitySnapshot;
  replaces: string | null;
  /** uid imposé (entité appariée dont la cible avait un uid). */
  forcedUid?: string;
  desiredObservation: string | null;
  desiredPhotos: number[];
}

/** Fusion 3 voies des photos d'un marqueur : triées, sans conflit possible. */
export function mergePhotoIds(
  base: readonly number[],
  target: readonly number[],
  merged: readonly number[],
): number[] {
  const inTarget = new Set(target);
  const inMerged = new Set(merged);
  const inBase = new Set(base);
  return [...new Set([...target, ...merged])]
    .filter((id) => !inBase.has(id) || (inTarget.has(id) && inMerged.has(id)))
    .sort((a, b) => a - b);
}

const sameIds = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export function buildMergePlan(
  analysis: MergeAnalysis,
  resolutions: MergeResolutions,
  options: MergePlanOptions = {},
): MergePlan {
  const unresolved = unresolvedConflicts(analysis, resolutions);
  if (unresolved.length) {
    throw new Error(
      `buildMergePlan : ${unresolved.length} conflit(s) non résolu(s) (${unresolved
        .map((c) => c.id)
        .join(", ")})`,
    );
  }
  const conflictById = new Map(analysis.conflicts.map((c) => [c.id, c]));

  const resolveGeometry = (d: EntityDecision): GeometryOutcome => {
    if (typeof d.geometry === "string") return d.geometry;
    const conflict = conflictById.get(d.geometry.conflictId) as MergeConflict;
    const kind = conflict.kind as keyof typeof GEOMETRY_BY_CHOICE;
    return GEOMETRY_BY_CHOICE[kind][resolutions[conflict.id]];
  };
  const resolveObservation = (d: EntityDecision): string | null => {
    if (d.observation === null || typeof d.observation === "string") {
      return d.observation;
    }
    return resolutions[d.observation.conflictId] === "merged"
      ? (d.merged?.observationId ?? null)
      : (d.target?.observationId ?? null);
  };

  const deletes: MergeOp[] = [];
  const uidOps: MergeOp[] = [];
  // Pas d'alias depuis un uid ambigu : l'activité de toutes les entités qui le portent
  // serait attribuée à une seule.
  const uidAliases: UidAlias[] = [];
  const result: ResultEntity[] = [];
  const pending: PendingCopy[] = [];
  const usedUids = new Set<string>();

  const keepTarget = (
    t: EntitySnapshot,
    desired: string | null,
    desiredPhotos: number[] = t.photoIds,
  ) => {
    if (t.uid) usedUids.add(t.uid);
    result.push({
      ref: { side: "target", handle: t.handle },
      currentObservation: t.observationId,
      desiredObservation: desired,
      currentPhotos: t.photoIds,
      desiredPhotos,
    });
  };

  // 1. Côté cible : ajouts propres à la cible, puis entités de la base.
  for (const t of analysis.addedInTarget) keepTarget(t, t.observationId);

  for (const d of analysis.decisions) {
    const geometry = resolveGeometry(d);
    const desired = resolveObservation(d);
    const t = d.target;
    const m = d.merged;
    const photos =
      t && m
        ? mergePhotoIds(d.base.photoIds, t.photoIds, m.photoIds)
        : undefined;

    switch (geometry) {
      case "target":
      case "both": {
        if (!t) break;
        keepTarget(t, desired, photos);
        if (geometry === "target" && m?.uid) {
          if (t.uid && t.uid !== m.uid) {
            if (!d.ambiguousUid) {
              uidAliases.push({ fromUid: m.uid, toUid: t.uid });
            }
          } else if (!t.uid) {
            uidOps.push({
              op: "setUid",
              ref: { side: "target", handle: t.handle },
              uid: m.uid,
            });
            usedUids.add(m.uid);
          }
        }
        // « Les deux » : la version fusionnée est ajoutée à côté, avec ses
        // propres events (pas d'alias) — sauf uid partagé, cf. étape 2.
        if (geometry === "both" && m) {
          pending.push({
            merged: m,
            replaces: null,
            desiredObservation: m.observationId,
            desiredPhotos: m.photoIds,
          });
        }
        break;
      }
      case "merged": {
        if (!t || !m) break;
        pending.push({
          merged: m,
          replaces: t.handle,
          forcedUid: t.uid ?? undefined,
          desiredObservation: desired,
          desiredPhotos: photos ?? m.photoIds,
        });
        if (t.uid && m.uid && t.uid !== m.uid && !d.ambiguousUid) {
          uidAliases.push({ fromUid: m.uid, toUid: t.uid });
        }
        break;
      }
      case "delete":
        if (t) deletes.push({ op: "delete", targetHandle: t.handle });
        break;
      case "restore":
        if (m)
          pending.push({
            merged: m,
            replaces: null,
            desiredObservation: desired,
            desiredPhotos: m.photoIds,
          });
        break;
      case "absent":
        break;
    }
  }

  for (const m of analysis.addedInMerged) {
    pending.push({
      merged: m,
      replaces: null,
      desiredObservation: m.observationId,
      desiredPhotos: m.photoIds,
    });
  }

  // 2. Copies depuis la branche fusionnée.
  const copies: MergeOp[] = [];
  for (const p of pending) {
    let uid: string | "fresh" | null;
    if (p.forcedUid) {
      uid = p.forcedUid;
      usedUids.add(p.forcedUid);
    } else if (p.merged.uid && usedUids.has(p.merged.uid)) {
      uid = "fresh";
    } else {
      uid = null;
      if (p.merged.uid) usedUids.add(p.merged.uid);
    }
    copies.push({
      op: "copy",
      mergedHandle: p.merged.handle,
      replaces: p.replaces,
      uid,
    });
    result.push({
      ref: { side: "merged", handle: p.merged.handle },
      currentObservation: p.merged.observationId,
      desiredObservation: p.desiredObservation,
      currentPhotos: p.merged.photoIds,
      desiredPhotos: p.desiredPhotos,
    });
  }

  // 3. Rattachements (dont observations supprimées entre-temps).
  const observationOps: MergeOp[] = [];
  let detachedFromDeletedObservations = 0;
  for (const r of result) {
    let desired = r.desiredObservation;
    if (
      desired &&
      options.validObservationIds &&
      !options.validObservationIds.has(desired)
    ) {
      desired = null;
      detachedFromDeletedObservations++;
    }
    if (desired !== r.currentObservation) {
      observationOps.push({
        op: "setObservation",
        ref: r.ref,
        observationId: desired,
      });
    }
  }

  const photoOps: MergeOp[] = result
    .filter((r) => !sameIds(r.currentPhotos, r.desiredPhotos))
    .map((r) => ({ op: "setPhotos", ref: r.ref, photoIds: r.desiredPhotos }));

  // 4. Éléments nommés : décidés sans conflit (cf. analyze.ts).
  const recordOps: MergeOp[] = analysis.recordActions.map((a) =>
    a.action === "delete"
      ? { op: "deleteLayout", name: a.name }
      : { op: "copyRecord", kind: a.kind, name: a.name },
  );
  const byDependency = (o: MergeOp) =>
    o.op === "copyRecord" ? RECORD_ORDER.indexOf(o.kind) : RECORD_ORDER.length;
  recordOps.sort((a, b) => byDependency(a) - byDependency(b));
  const isLayoutOp = (o: MergeOp) =>
    o.op === "deleteLayout" || (o.op === "copyRecord" && o.kind === "layout");

  return {
    // Styles, calques et blocs d'abord (les copies d'entités y retrouvent leurs
    // définitions à jour), copies avant les opérations qui visent une entité
    // copiée, présentations à la fin.
    ops: [
      ...recordOps.filter((o) => !isLayoutOp(o)),
      ...deletes,
      ...uidOps,
      ...copies,
      ...observationOps,
      ...photoOps,
      ...recordOps.filter(isLayoutOp),
    ],
    uidAliases,
    detachedFromDeletedObservations,
  };
}
